import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, BookOpen, ArrowRight } from "lucide-react";
import { KAJABI } from "@/lib/data";

const DISMISS_KEY = "um_exit_modal_dismissed";

export default function ExitIntentModal() {
 const [open, setOpen] = useState(false);

 useEffect(() => {
 if (typeof window === "undefined") return;
 if (sessionStorage.getItem(DISMISS_KEY)) return;

 let triggered = false;

 // Desktop: mouse leaves top of viewport
 const onMouseOut = (e) => {
 if (triggered) return;
 if (e.clientY <= 0 && !e.relatedTarget) {
 triggered = true;
 setOpen(true);
 }
 };

 // Mobile fallback: scroll depth > 60% after 20s dwell
 const onScrollFallback = () => {
 if (triggered) return;
 const scrollPct =
 (window.scrollY + window.innerHeight) /
 document.documentElement.scrollHeight;
 if (scrollPct > 0.6) {
 triggered = true;
 setOpen(true);
 }
 };

 document.addEventListener("mouseout", onMouseOut);
 const timer = setTimeout(() => {
 window.addEventListener("scroll", onScrollFallback, { passive: true });
 }, 20000);

 return () => {
 document.removeEventListener("mouseout", onMouseOut);
 window.removeEventListener("scroll", onScrollFallback);
 clearTimeout(timer);
 };
 }, []);

 const close = () => {
 setOpen(false);
 sessionStorage.setItem(DISMISS_KEY, "1");
 };

 const handleGetGuide = () => {
 sessionStorage.setItem(DISMISS_KEY, "1");
 window.open(KAJABI.ebook, "_blank", "noopener,noreferrer");
 setOpen(false);
 };

 return (
 <AnimatePresence>
 {open && (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.25 }}
 data-testid="exit-intent-modal"
 className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
 onClick={close}
 >
 <motion.div
 initial={{ scale: 0.95, y: 20 }}
 animate={{ scale: 1, y: 0 }}
 exit={{ scale: 0.95, y: 20 }}
 transition={{ duration: 0.3, ease: "easeOut" }}
 onClick={(e) => e.stopPropagation()}
 className="relative w-full max-w-xl bg-ink-900 border border-amber/40 shadow-[0_20px_80px_-20px_rgba(34,197,94,0.3)] p-8 md:p-12"
 >
 <button
 type="button"
 data-testid="exit-intent-close"
 onClick={close}
 className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors"
 aria-label="Close"
 >
 <X size={20} />
 </button>

 <div className="flex items-center gap-3 mb-4">
 <div className="h-10 w-10 rounded-sm bg-amber/15 border border-amber/40 flex items-center justify-center text-amber">
 <BookOpen size={18} />
 </div>
 <span className="font-mono text-xs uppercase tracking-[0.25em] text-amber font-semibold">
 Wait · before you go
 </span>
 </div>

 <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.05]">
 Grab the Foundations of Sync Success{" "}
 <span className="text-amber italic">Quickstart.</span>
 </h3>
 <p className="mt-4 text-white/70 text-base leading-relaxed">
 The fastest way to understand how sync licensing actually works - 
 straight from Devon &amp; Becki. Free. No strings.
 </p>

 <button
 type="button"
 onClick={handleGetGuide}
 data-testid="exit-intent-get-guide"
 className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-wider text-xs px-6 py-5 rounded-sm transition-all group"
 >
 Get the Free Quickstart
 <ArrowRight
 size={14}
 className="transition-transform group-hover:translate-x-1"
 />
 </button>

 <button
 type="button"
 data-testid="exit-intent-no-thanks"
 onClick={close}
 className="mt-4 text-xs text-white/40 hover:text-white/70 font-mono uppercase tracking-widest"
 >
 No thanks. I'm ready to commit
 </button>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>
 );
}
